import { FilterParameters } from "_shared/_api/queries/filtering/filter-parameters";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import FeeSchedulesFilterSchema, {
  FeeSchedulesFilterJSON,
  FeeSchedulesFilterParams,
} from "work/fee-schedules/_api/queries/contracts/filtering/_schema/fee-schedules-filter-schema";

export default class FeeSchedulesFilterParameters extends FilterParameters {
  public static readonly defaultOptions = {
    queryParamName: "query",
    entityIdParamName: "entityId",
    minCreatedDateParamName: "minCreatedDate",
    maxCreatedDateParamName: "maxCreatedDate",
    isArchivedParamName: "isArchived",
    isActiveParamName: "isActive",
    clientTemplateIdsParamName: "clientTemplateIds",
    vendorTemplateIdsParamName: "vendorTemplateIds",
  };

  public static fromJSON(json: FeeSchedulesFilterJSON): FeeSchedulesFilterParameters {
    const params = FeeSchedulesFilterSchema.deserialize(json);
    return new FeeSchedulesFilterParameters(params);
  }

  public static fromSearchParams(args: {
    searchParams: URLSearchParams;
    session: Session;
    options?: {
      queryParamName?: string;
      entityIdParamName?: string;
      minCreatedDateParamName?: string;
      maxCreatedDateParamName?: string;
      isArchivedParamName?: string;
      isActiveParamName?: string;
      clientTemplateIdsParamName?: string;
      vendorTemplateIdsParamName?: string;
    };
  }): FeeSchedulesFilterParameters {
    const { searchParams, session, options } = args;
    const paramNames = { ...FeeSchedulesFilterParameters.defaultOptions, ...options };
    const entityId = Guid.fromJSON(searchParams.get(paramNames.entityIdParamName)) ?? session.user!.legalEntityId!;

    return new FeeSchedulesFilterParameters({
      entityId,
      query: searchParams.get(paramNames.queryParamName) ?? undefined,
      minCreatedDate: searchParams.get(paramNames.minCreatedDateParamName)
        ? new AHDate(searchParams.get(paramNames.minCreatedDateParamName)!)
        : undefined,
      maxCreatedDate: searchParams.get(paramNames.maxCreatedDateParamName)
        ? new AHDate(searchParams.get(paramNames.maxCreatedDateParamName)!)
        : undefined,
      isArchived: new AHBoolean(searchParams.get(paramNames.isArchivedParamName) === "true"),
      isActive: searchParams.get(paramNames.isActiveParamName)
        ? new AHBoolean(searchParams.get(paramNames.isActiveParamName) === "true")
        : undefined,
      clientTemplateIds:
        searchParams.getAll(paramNames.clientTemplateIdsParamName)?.map((id) => Guid.fromJSON(id)!).filter(Boolean) ??
        undefined,
      vendorTemplateIds:
        searchParams.getAll(paramNames.vendorTemplateIdsParamName)?.map((id) => Guid.fromJSON(id)!).filter(Boolean) ??
        undefined,
    });
  }

  public query?: string;
  public entityId: Guid;
  public minCreatedDate?: AHDate;
  public maxCreatedDate?: AHDate;
  public isArchived: AHBoolean;
  public isActive?: AHBoolean;
  public clientTemplateIds?: Guid[];
  public vendorTemplateIds?: Guid[];

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.query) searchParams.append("query", this.query);
    if (this.entityId) searchParams.append("entityId", this.entityId.toJSON());
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.isArchived !== undefined) searchParams.append("isArchived", this.isArchived.toString());
    if (this.isActive !== undefined) searchParams.append("isActive", this.isActive.toString());
    if (this.clientTemplateIds) {
      for (const clientTemplateId of this.clientTemplateIds) {
        searchParams.append("clientTemplateIds", clientTemplateId.toJSON());
      }
    }
    if (this.vendorTemplateIds) {
      for (const vendorTemplateId of this.vendorTemplateIds) {
        searchParams.append("vendorTemplateIds", vendorTemplateId.toJSON());
      }
    }
    return searchParams;
  }

  public constructor(params: FeeSchedulesFilterParams) {
    super();
    Object.assign(this, params);
    this.entityId = params.entityId;
    this.isArchived = params.isArchived ?? new AHBoolean(false);
  }

  public isEqualTo(other: FeeSchedulesFilterParameters): boolean {
    if (!other || !(other instanceof FeeSchedulesFilterParameters)) return false;
    return Boolean(
      this.query === other.query &&
        this.entityId.isEqualTo(other.entityId) &&
        (this.minCreatedDate?.isEqualTo(other.minCreatedDate) ?? this.minCreatedDate === other.minCreatedDate) &&
        (this.maxCreatedDate?.isEqualTo(other.maxCreatedDate) ?? this.maxCreatedDate === other.maxCreatedDate) &&
        this.isArchived.isEqualTo(other.isArchived) &&
        (this.isActive?.isEqualTo(other.isActive) ?? this.isActive === other.isActive) &&
        JSON.stringify(this.clientTemplateIds?.map((id) => id.value)) ===
          JSON.stringify(other.clientTemplateIds?.map((id) => id.value)) &&
        JSON.stringify(this.vendorTemplateIds?.map((id) => id.value)) ===
          JSON.stringify(other.vendorTemplateIds?.map((id) => id.value))
    );
  }
}